import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import { ExpenseForm } from "../components/ExpenseForm";
import {
  defaultScheduleValue,
  RecurringScheduleFields,
  toUtcHourlyIso,
  type RecurringScheduleValue,
} from "../components/RecurringScheduleFields";
import { useCreateRecurringExpense, useGroup, useMe } from "../api/hooks";
import { getMyParticipantId } from "../lib/groupRole";
import type { Group } from "../api/types";

export function CreateRecurringExpensePage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const groupQ = useGroup(groupId);

  if (groupQ.isLoading || !groupQ.data) {
    return (
      <div className="mx-auto max-w-2xl space-y-stack-lg">
        <Skeleton className="h-64" />
      </div>
    );
  }
  return <CreateRecurring group={groupQ.data} />;
}

function CreateRecurring({ group }: { group: Group }): ReactNode {
  const navigate = useNavigate();
  const meQ = useMe();
  const createRecurring = useCreateRecurringExpense(group.id);
  const [schedule, setSchedule] = useState<RecurringScheduleValue>(() =>
    defaultScheduleValue(),
  );
  const groupPath = `/groups/${group.id}`;
  const participantsKey = group.participants
    .map((p) => p.id)
    .sort()
    .join("|");

  const utcIso = toUtcHourlyIso(schedule.nextRunLocal);

  return (
    <div className="mx-auto max-w-2xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(groupPath) }}
        title="New recurring expense"
        description="We'll create a real expense in this group on the schedule below. Minimum cadence is once a week."
      />
      <ExpenseForm
        key={participantsKey}
        group={group}
        showExpenseDate={false}
        defaultPayerId={getMyParticipantId(group, meQ.data)}
        submitLabel="Save recurring"
        pendingLabel="Saving…"
        submitting={createRecurring.isPending}
        serverError={
          createRecurring.error
            ? "Failed to create recurring expense. Please try again."
            : null
        }
        extraValid={utcIso !== null && schedule.intervalCount >= 1}
        extraFields={
          <RecurringScheduleFields value={schedule} onChange={setSchedule} />
        }
        onCancel={() => navigate(groupPath)}
        onSubmit={async (values) => {
          if (!utcIso) return;
          await createRecurring.mutateAsync({
            label: values.label,
            currency: values.currency,
            payments: values.payments,
            items: values.items.map((it) => ({
              label: it.label,
              cost: it.cost,
              split: it.split,
            })),
            unit: schedule.unit,
            intervalCount: schedule.intervalCount,
            nextRunAt: utcIso,
          });
          navigate(groupPath);
        }}
      />
    </div>
  );
}
